import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowCounterClockwise, CaretLeft } from "@phosphor-icons/react";
import { QUIZ_QUESTIONS, MEDICAL_RESULT, resolveResult } from "@/data/quiz";
import { PROGRAMS } from "@/components/site/Programs";
import { trackEvent } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

function AiSummary({ answers, resultTitle, onSummary }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const payload = {
      result_title: resultTitle,
      answers: QUIZ_QUESTIONS.map((q, i) => ({ question: q.title, answer: answers[i].label })),
    };
    fetch(`${API}/api/quiz-summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!cancelled) {
          setSummary(d.summary);
          if (onSummary) onSummary(d.summary);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [answers, onSummary, resultTitle]);

  return (
    <div data-testid="quiz-ai-summary" className="mt-6 border-t border-border pt-5">
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-accent font-semibold">
        Your personalized read · AI
      </div>
      {loading ? (
        <div className="mt-3 space-y-2" aria-label="Generating your personalized summary">
          <div className="h-3 bg-border/70 animate-pulse w-full" />
          <div className="h-3 bg-border/70 animate-pulse w-11/12" />
          <div className="h-3 bg-border/70 animate-pulse w-4/5" />
        </div>
      ) : failed ? (
        <p data-testid="quiz-ai-summary-error" className="mt-3 text-sm text-foreground/55 leading-relaxed">
          Your personalized summary couldn't load just now — your match above still stands, and the
          health assessment will give you the full picture.
        </p>
      ) : (
        <p className="mt-3 text-sm md:text-base text-foreground/75 leading-relaxed">{summary}</p>
      )}
    </div>
  );
}

function EmailCapture({ resultTitle, resultHref, summary, answers }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/quiz-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: value,
          result_title: resultTitle,
          result_href: resultHref,
          summary: summary || "",
          answers: QUIZ_QUESTIONS.map((q, i) => ({ question: q.title, answer: answers[i].label })),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("sent");
      trackEvent("quiz_email_submitted", { result: resultTitle });
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <p
        data-testid="quiz-email-success"
        className="mt-5 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-primary font-semibold"
      >
        ✓ Sent — check your inbox
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5">
      <label
        htmlFor="quiz-email"
        className="block font-mono text-[0.6rem] uppercase tracking-[0.22em] text-foreground/60 font-semibold"
      >
        Email me my results
      </label>
      <div className="mt-2 flex gap-2">
        <input
          id="quiz-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="quiz-email-input"
          placeholder="you@example.com"
          className="flex-1 min-w-0 border border-border bg-background px-3.5 py-2.5 text-sm text-foreground
                     placeholder:text-foreground/40 outline-none focus:border-primary transition-colors"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="quiz-email-submit"
          className="iw-btn-red px-5 py-2.5 text-[0.62rem] disabled:opacity-50"
        >
          {status === "sending" ? "Sending…" : "Send"}
        </button>
      </div>
      {status === "error" && (
        <p data-testid="quiz-email-error" className="mt-2 text-xs text-destructive">
          Couldn't send just now — please try again.
        </p>
      )}
    </form>
  );
}

function QuizResult({ result, answers, onRestart }) {
  const [aiSummary, setAiSummary] = useState("");
  const program = result.medical ? null : PROGRAMS.find((p) => p.slug === result.slug);
  const rec = result.medical
    ? MEDICAL_RESULT
    : {
        title: program.title,
        pitch: program.pitch,
        image: program.image,
        href: `/programs/${program.slug}`,
        linkLabel: `Explore ${program.title}`,
      };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="quiz-result"
      className="border-2 border-primary/30 bg-card overflow-hidden"
    >
      <div className="grid grid-cols-1 sm:grid-cols-12 items-stretch">
        <div className="sm:col-span-5 relative aspect-[4/3] sm:aspect-auto overflow-hidden bg-foreground">
          <img
            src={rec.image}
            alt={`${rec.title} — your recommended Integrated Wellness pathway`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/40 to-transparent pointer-events-none" />
        </div>
        <div className="sm:col-span-7 p-7 md:p-9">
          <div className="iw-overline">Your Match</div>
          <h2
            data-testid="quiz-result-title"
            className="mt-3 font-display text-2xl md:text-3xl font-bold tracking-tight text-primary leading-tight"
          >
            {rec.title}
          </h2>
          <p className="mt-4 text-base text-foreground/75 leading-relaxed">{rec.pitch}</p>
          <AiSummary answers={answers} resultTitle={rec.title} onSummary={setAiSummary} />
          <EmailCapture
            resultTitle={rec.title}
            resultHref={rec.href}
            summary={aiSummary}
            answers={answers}
          />
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="/get-started"
              data-testid="quiz-result-cta"
              className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
            >
              Start My Health Assessment <span aria-hidden>→</span>
            </a>
            <a
              href={rec.href}
              data-testid="quiz-result-program-link"
              className="iw-btn-ghost inline-flex items-center gap-2 px-6 py-3.5 text-[0.65rem]"
            >
              {rec.linkLabel}
            </a>
          </div>
          <button
            type="button"
            onClick={onRestart}
            data-testid="quiz-restart-btn"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-foreground/55 hover:text-primary transition-colors"
          >
            <ArrowCounterClockwise size={14} /> Retake the quiz
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function QuizEngine() {
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const total = QUIZ_QUESTIONS.length;
  const step = answers.length;

  const pick = (option) => {
    if (step === 0) trackEvent("quiz_started", {});
    const next = [...answers, option];
    setAnswers(next);
    if (next.length === total) {
      const r = resolveResult(next);
      setResult(r);
      trackEvent("quiz_completed", { result: r.medical ? "medical-care" : r.slug });
    }
  };

  const back = () => {
    setResult(null);
    setAnswers(answers.slice(0, -1));
  };

  const restart = () => {
    setResult(null);
    setAnswers([]);
  };

  if (result) return <QuizResult result={result} answers={answers} onRestart={restart} />;

  const q = QUIZ_QUESTIONS[step];

  return (
    <div data-testid="quiz-engine" className="border border-border bg-card p-7 md:p-10">
      {/* Progress */}
      <div className="flex items-center justify-between">
        <div data-testid="quiz-progress" className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-primary font-semibold">
          Question {step + 1} / {total}
        </div>
        {step > 0 && (
          <button
            type="button"
            onClick={back}
            data-testid="quiz-back-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-foreground/55 hover:text-primary transition-colors"
          >
            <CaretLeft size={12} /> Back
          </button>
        )}
      </div>
      <div className="mt-3 h-1 bg-border overflow-hidden">
        <div
          className="h-full bg-accent transition-all duration-500"
          style={{ width: `${(step / total) * 100}%` }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.3 }}
        >
          <h2
            data-testid="quiz-question-title"
            className="mt-8 font-display text-xl md:text-2xl font-bold tracking-tight text-primary leading-snug"
          >
            {q.title}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-2.5">
            {q.options.map((opt, i) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => pick(opt)}
                data-testid={`quiz-option-${i}`}
                className="group text-left border border-border bg-background px-5 py-4
                           hover:border-primary hover:bg-primary/[0.04] transition-all
                           flex items-center justify-between gap-4"
              >
                <span className="text-base text-foreground/85 leading-snug">{opt.label}</span>
                <span
                  aria-hidden
                  className="font-mono text-accent opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  →
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
